// @flow
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as DmsCommon from './../supports/dms-common';
import * as DmsService from './../supports/dms-service';
import MsgDialog from './MsgDialog';
import SelFolderDialog from './SelFolderDialog';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import SaveIcon from '@mui/icons-material/Save';

import { useForm, Controller } from "react-hook-form";
import DatePicker from '@mui/lab/DatePicker';

export default function DocInfoFormContent(props) {
  const {
    sysID,
    sessionID,
    docDetail,
    docTypes,
    folders,
    onCatchPromiseError,
    afterDocDetailUpdate,
    onRequestClose,
    readonly,
    alwayEdit,
    compactMode,
    notInDialog,
  } = props;

  const [openFolderDialog, setOpenFolderDialog] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [selFolderID, setSelFolderID] = useState(
    docDetail?.FolderID == undefined
      ? DmsCommon.VOID_FOLDER_ID
      : docDetail?.FolderID
  );

  const [selectedDocTypes, setSelectedDocTypes] = useState(
    (docTypes || []).filter((d) => (docDetail.DocTypeCodes || []).indexOf(d.RefCode) > -1)
  );

  const { control, handleSubmit, setValue, reset } = useForm({
    defaultValues: {
      folderID: docDetail.FolderID,
      folderPath: docDetail.FolderPath,
      docDate: docDetail.DocDate,
      docRef: docDetail.DocRef,
      docType: docDetail.DocType,
      ourRef: docDetail.OurRef,
      subject: docDetail.Subject,
      recDate: docDetail.RecDate,
      RFID: docDetail.RFID,
      fileLoc: docDetail.FileLocation,
      sender: docDetail.Sender,
      keywords: docDetail.Keywords,
      compDept: docDetail.Company,
      recipient: docDetail.Recipient,
    }
  });

  useEffect(() => {
    if (!docDetail) return;

    reset({
      folderID: docDetail.FolderID,
      folderPath: docDetail.FolderPath,
      docDate: docDetail.DocDate,
      docRef: docDetail.DocRef,
      docType: docDetail.DocType,
      ourRef: docDetail.OurRef,
      subject: docDetail.Subject,
      recDate: docDetail.RecDate,
      RFID: docDetail.RFID,
      fileLoc: docDetail.FileLocation,
      sender: docDetail.Sender,
      keywords: docDetail.Keywords,
      compDept: docDetail.Company,
      recipient: docDetail.Recipient,
    });

    setSelFolderID(
      docDetail?.FolderID == undefined
        ? DmsCommon.VOID_FOLDER_ID
        : docDetail.FolderID
    );

    setSelectedDocTypes(
      (docTypes || []).filter((d) =>
        (docDetail.DocTypeCodes || []).indexOf(d.RefCode) > -1
      )
    );
  }, [docDetail, docTypes, reset]);

  const fieldSize = compactMode ? "small" : undefined;
  const readOnlySx = {
    "& .MuiInputBase-input": {
      fontSize: compactMode ? 11 : undefined,
    },
  };

  let RF_UpdateTimelapseDisplay = "";
  if (docDetail.PhyLocTimelapse > 0 && docDetail.PhyLoc !== "") {
    let timeDiff = docDetail.PhyLocTimelapse;

    let seconds = Math.round(timeDiff % 60);
    timeDiff = Math.floor(timeDiff / 60);

    let minutes = Math.round(timeDiff % 60);
    timeDiff = Math.floor(timeDiff / 60);

    let hours = Math.round(timeDiff % 24);
    timeDiff = Math.floor(timeDiff / 24);

    let days = timeDiff;

    if (days > 0) {
      RF_UpdateTimelapseDisplay =
        ` [${docDetail.PhyLoc} (${days} ${days > 1 ? "days" : "day"} ago)]`;
    } else if (hours > 0) {
      RF_UpdateTimelapseDisplay =
        ` [${docDetail.PhyLoc} (${hours} ${hours > 1 ? "hrs" : "hr"} ago)]`;
    } else if (minutes > 0) {
      RF_UpdateTimelapseDisplay =
        ` [${docDetail.PhyLoc} (${minutes} ${minutes > 1 ? "mins" : "min"} ago)]`;
    } else if (seconds > 0) {
      RF_UpdateTimelapseDisplay =
        ` [${docDetail.PhyLoc} (${seconds} ${seconds > 1 ? "seconds" : "second"} ago)]`;
    }
  }

  const handleSelectFolderID = (aFolderID) => {
    setOpenFolderDialog(false);

    const vSelFolder = folders.find((f) => f.id === aFolderID);

    if (vSelFolder) {
      setSelFolderID(aFolderID);
      setValue("folderID", vSelFolder.id, { shouldDirty: true });
      setValue("folderPath", vSelFolder.FolderPath, { shouldDirty: true });
    }
  };

  const handleChangeDocType = (event) => {
    setSelectedDocTypes(event.target.value);
  };

  const onSubmit = (data) => {
    if (readonly) return;

    const xFunc = async () => {
      setShowLoading(true);

      try {
        const docDateISO =
          data.docDate?.format(DmsCommon.ISO_DATETIME_FORMAT) || "";
        const recDateISO =
          data.recDate?.format(DmsCommon.ISO_DATETIME_FORMAT) || "";

        const docTypeCodes = selectedDocTypes.map((d) => d.RefCode);

        await DmsService.docUpdatePromise(
          sessionID,
          sysID,
          docDetail.DocID,
          data.folderID,
          docDateISO,
          docTypeCodes,
          data.subject,
          data.sender,
          data.compDept,
          data.recipient,
          data.docRef,
          data.ourRef,
          data.fileLoc,
          data.keywords,
          data.RFID,
          recDateISO
        );

        setShowLoading(false);
        afterDocDetailUpdate && afterDocDetailUpdate();
        onRequestClose && onRequestClose();
      } catch (e) {
        setShowLoading(false);

        if (onCatchPromiseError) {
          onCatchPromiseError(e);
        } else {
          setErrMsg(e.errMsg || e.message || "Unknown error");
          setShowError(true);
        }
      }
    };

    xFunc();
  };

  const textFieldCommonProps = readonly
    ? {
      InputProps: { readOnly: true },
      InputLabelProps: { shrink: true },
      size: fieldSize,
      sx: readOnlySx,
    }
    : {
      InputLabelProps: { shrink: true },
    };

  const docTypeDisplay = selectedDocTypes.map((d) => d.RefCodeDesc).join(', ');

  let itemOpt3 = {};
  if (!notInDialog) {
    itemOpt3["sm"] = 3;
  }

  let itemOpt6 = {};
  if (!notInDialog) {
    itemOpt6["sm"] = 6;
  }

  return (
    <>
      {!readonly && (
        <>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={showLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          <MsgDialog
            open={showError}
            title="Error"
            msgText={errMsg}
            onClose={() => setShowError(false)}
          />

          <SelFolderDialog
            open={openFolderDialog}
            onClose={() => setOpenFolderDialog(false)}
            folders={folders}
            iniFolderID={selFolderID}
            disableAllFolder
            disablePhyLocFolder
            onSelectFolderID={handleSelectFolderID}
          />
        </>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={{ xs: 1 }}>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Controller
              name="subject"
              control={control}
              render={({ field }) => (
                <TextField
                  required={!readonly}
                  label="Subject"
                  variant={readonly ? undefined : "standard"}
                  fullWidth
                  {...field}
                  {...textFieldCommonProps}
                  inputProps={{ maxLength: 200 }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="folderPath"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Folder"
                  variant={readonly ? undefined : "standard"}
                  required={!readonly}
                  fullWidth
                  {...field}
                  {...(readonly
                    ? textFieldCommonProps
                    : {
                      InputLabelProps: { shrink: true },
                      InputProps: {
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setOpenFolderDialog(true)}
                              onMouseDown={(event) => event.preventDefault()}
                              edge="end"
                            >
                              <FolderOpenIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    })}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} {...itemOpt3}>
            <Controller
              name="docDate"
              control={control}
              render={({ field }) =>
                readonly ? (
                  <TextField
                    label="Document Date"
                    fullWidth
                    value={field.value ? field.value.format(DmsCommon.SHORT_DATE_FORMAT) : ""}
                    {...textFieldCommonProps}
                  />
                ) : (
                  <DatePicker
                    label="Document Date"
                    type="date"
                    inputFormat={DmsCommon.SHORT_DATE_FORMAT}
                    {...field}
                    renderInput={(params) => (
                      <TextField
                        variant="standard"
                        required
                        fullWidth
                        {...params}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                )
              }
            />
          </Grid>

          <Grid item xs={12} {...itemOpt3}>
            <Controller
              name="recDate"
              control={control}
              render={({ field }) =>
                readonly ? (
                  <TextField
                    label="Receive Date"
                    fullWidth
                    value={field.value ? field.value.format(DmsCommon.SHORT_DATE_FORMAT) : ""}
                    {...textFieldCommonProps}
                  />
                ) : (
                  <DatePicker
                    label="Receive Date"
                    type="date"
                    inputFormat={DmsCommon.SHORT_DATE_FORMAT}
                    {...field}
                    renderInput={(params) => (
                      <TextField
                        variant="standard"
                        fullWidth
                        {...params}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                )
              }
            />
          </Grid>

          <Grid item xs={12} {...itemOpt6}>
            <Controller
              name="docRef"
              control={control}
              render={({ field }) => (
                <TextField
                  label={readonly ? "Ref. No." : "Doc Ref"}
                  variant={readonly ? undefined : "standard"}
                  fullWidth
                  {...field}
                  {...textFieldCommonProps}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} {...itemOpt6}>
            {readonly ? (
              <TextField
                label="Document Type"
                fullWidth
                value={docTypeDisplay}
                {...textFieldCommonProps}
              />
            ) : (
              <TextField
                label="Document Types"
                variant="standard"
                value={selectedDocTypes}
                onChange={handleChangeDocType}
                select
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) =>
                    selected.map((f) => f.RefCodeDesc).join(', '),
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              >
                {docTypes.map((d) => (
                  <MenuItem key={d.RefCode} value={d}>
                    <Checkbox checked={selectedDocTypes.indexOf(d) > -1} />
                    <ListItemText primary={d.RefCodeDesc} />
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>

          <Grid item xs={12} {...itemOpt6}>
            <Controller
              name="ourRef"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Our Ref No"
                  variant={readonly ? undefined : "standard"}
                  fullWidth
                  {...field}
                  {...textFieldCommonProps}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} {...itemOpt6} container spacing={1}>
            <Grid item xs={12}>
              <Controller
                name="RFID"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="RFID"
                    variant={readonly ? undefined : "standard"}
                    fullWidth
                    {...field}
                    {...textFieldCommonProps}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="fileLoc"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="File Location"
                    variant={readonly ? undefined : "standard"}
                    fullWidth
                    value={readonly ? (docDetail.FileLocation + RF_UpdateTimelapseDisplay) : field.value}
                    {...(readonly ? textFieldCommonProps : {
                      ...field,
                      InputLabelProps: { shrink: true }
                    })}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="sender"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Sender"
                    variant={readonly ? undefined : "standard"}
                    fullWidth
                    {...field}
                    {...textFieldCommonProps}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} {...itemOpt6}>
            <Controller
              name="keywords"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Keywords"
                  variant={readonly ? undefined : "standard"}
                  fullWidth
                  multiline
                  minRows={readonly ? (compactMode ? 3.5 : 6.5) : 6}
                  maxRows={readonly ? (compactMode ? 3.5 : 6.5) : 6}
                  {...field}
                  {...textFieldCommonProps}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} {...itemOpt6}>
            <Controller
              name="compDept"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Company / Department"
                  variant={readonly ? undefined : "standard"}
                  fullWidth
                  {...field}
                  {...textFieldCommonProps}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} {...itemOpt6}>
            <TextField
              label="Upload By"
              variant={readonly ? undefined : "filled"}
              size={readonly ? fieldSize : "small"}
              fullWidth
              value={docDetail.UploadByUser}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={readonly ? readOnlySx : undefined}
            />
          </Grid>

          <Grid item xs={12} {...itemOpt6}>
            <Controller
              name="recipient"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Recipient"
                  variant={readonly ? undefined : "standard"}
                  fullWidth
                  {...field}
                  {...textFieldCommonProps}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} {...itemOpt6}>
            <TextField
              label="Upload Date"
              variant={readonly ? undefined : "filled"}
              size={readonly ? fieldSize : "small"}
              fullWidth
              value={
                docDetail.CreateDate
                  ? docDetail.CreateDate.format(DmsCommon.SHORT_DATETIME_FORMAT)
                  : ""
              }
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={readonly ? readOnlySx : undefined}
            />
          </Grid>

          {!readonly && (
            <Grid item xs={12} sx={{ textAlign: 'right', mt: 2 }}>
              {alwayEdit && (
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                >
                  Update
                </Button>
              )}
              {!alwayEdit && (
                <>
                  <Button onClick={onRequestClose} sx={{ mr: 1 }}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                  >
                    Save
                  </Button>
                </>
              )}

            </Grid>
          )}
        </Grid>
      </form>
    </>
  );
}

DocInfoFormContent.propTypes = {
  sysID: PropTypes.string,
  sessionID: PropTypes.string,
  docDetail: PropTypes.object.isRequired,
  docTypes: PropTypes.array,
  folders: PropTypes.array,
  onCatchPromiseError: PropTypes.func,
  afterDocDetailUpdate: PropTypes.func,
  onRequestClose: PropTypes.func,
  readonly: PropTypes.bool,
  alwayEdit: PropTypes.bool,
  compactMode: PropTypes.bool,
  notInDialog: PropTypes.bool,
};

DocInfoFormContent.defaultProps = {
  docTypes: [],
  folders: [],
  readonly: false,
  alwayEdit: false,
  compactMode: false,
  notInDialog: false,
};